import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AirflowDagRunState } from '../../types';
import { formatDateTime, formatDurationSeconds } from '../../utils';

export type InstanceChartPoint = {
    runId: string;
    label?: string;
    queuedDurationSec: number;
    runDurationSec: number;
    state: AirflowDagRunState;
};

const STATE_FILL: Record<string, string> = {
    success: '#08c46a',
    running: '#1890ff',
    queued: '#fcad01',
};

function InstanceTooltip({ active, payload }: any) {
    if (!active || !payload?.length) return null;
    const point: InstanceChartPoint = payload[0].payload;
    return (
        <div style={{ background: '#212233', border: '1px solid #3e4161', borderRadius: 6, padding: 8, fontSize: 12, color: '#e1e1e6' }}>
            <div>{formatDateTime(point.label)}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, background: '#666a8d', opacity: 0.6 }} />
                {`Queued: ${formatDurationSeconds(point.queuedDurationSec)}`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, background: STATE_FILL[point.state] ?? '#ff4d4f' }} />
                {`Run: ${formatDurationSeconds(point.runDurationSec)}`}
            </div>
            <div style={{ fontFamily: 'monospace', color: '#666a8d' }}>{point.runId.slice(0, 24)}</div>
        </div>
    );
}

/*
 * Stacked bar chart of the last N task instances' queued + run durations, colored by state
 */
export function InstancesChart({ points }: { points: InstanceChartPoint[] }) {
    if (points.length === 0) {
        return <div style={{ color: '#666a8d', padding: 16 }}>No recent task instances to chart.</div>;
    }

    return (
        <ResponsiveContainer width="100%" height={260}>
            <BarChart data={points} margin={{ top: 8, right: 8, bottom: 8, left: 8 }} barSize={14} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#3e4161" strokeOpacity={0.7} />
                <XAxis dataKey="runId" hide />
                <YAxis tickFormatter={(v) => formatDurationSeconds(v)} width={60} tick={{ fontSize: 11, fill: '#666a8d' }} axisLine={false} tickLine={false} />
                <Tooltip content={<InstanceTooltip />} cursor={{ fill: '#3e4161', opacity: 0.5 }} />
                <Legend
                    iconType="square"
                    iconSize={10}
                    formatter={(value) => (value === 'queuedDurationSec' ? 'Queued Duration' : 'Run Duration')}
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                />
                <Bar dataKey="queuedDurationSec" stackId="a" fill="#666a8d" fillOpacity={0.5} isAnimationActive={false} />
                <Bar dataKey="runDurationSec" stackId="a" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {points.map((point) => (
                        <Cell key={point.runId} fill={STATE_FILL[point.state] ?? '#ff4d4f'} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}
